import { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import styles from "./index.module.css";
import Popup from "./CategoryPopup";
import { deleteObject, ref } from "firebase/storage";
import { storage, db, auth } from "../../firebase";
import { collection, getDocs, getDoc, doc } from "firebase/firestore";
import { ThemeContext } from "../../context/ThemeContext";
import { authenticatedFetch } from "../../services/apiClient";
import lightLogo from "../home/logolong.png";
import darkLogo from "../home/OLDlogolong.png";

const Form4 = (props) => {
  const navigate = useNavigate();
  const [logo, setLogo] = useState(lightLogo);
  const [categories, setCategories] = useState([]);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [value, setValue] = useState("");
  const [valueType, setValueType] = useState("");
  const [currentCategoryId, setCurrentCategoryId] = useState(null);
  const [error, setError] = useState("");
  const [commonCategories, setCommonCategories] = useState([]);
  const [featureCounts, setFeatureCounts] = useState({});
  const [totalUserCount, setTotalUserCount] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const { isDarkMode } = useContext(ThemeContext);
  const [theme, setTheme] = useState(isDarkMode);

  let BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  useEffect(() => {
    setTheme(isDarkMode);
    setLogo(isDarkMode ? darkLogo : lightLogo);
  }, [isDarkMode]);
  
  const fetchFeatureCounts = async () => {
    try {
      const categoryNames = categories.map((c) => c.name);
      const query = new URLSearchParams({
        categories: JSON.stringify(categoryNames),
        totals: "true", // Request non-timebound total demographic counts (commonCategoryCountTotal)
      });

      const res = await authenticatedFetch(
        `${BACKEND_URL}/api/project/get_feature_counts?${query.toString()}`,
        {
          method: "GET",
        }
      );

      const result = await res.json();

      if (result.success) {
        const normalizeName = (name) =>
          String(name || "")
            .trim()
            .toLowerCase();

        // Preferred shape: data.featureCounts = [{ categoryName, featuresName, count }]
        const featureCountsArray =
          result.data?.featureCounts || result.featureCounts || [];

        // Build a category -> feature -> count lookup for fast reads while rendering.
        const countsByCategory = {};

        if (Array.isArray(featureCountsArray) && featureCountsArray.length > 0) {
          featureCountsArray.forEach((item) => {
            const categoryName = item?.categoryName;
            const featuresName = item?.featuresName;
            const count = Number(item?.count) || 0;

            if (!categoryName || !featuresName) {
              return;
            }

            if (!countsByCategory[categoryName]) {
              countsByCategory[categoryName] = {};
            }

            countsByCategory[categoryName][featuresName] = count;
          });
        } else {
          // Backward-compatible fallback for old shape: data.counts[category][feature] = count
          const legacyCounts = result.data?.counts || {};
          Object.assign(countsByCategory, legacyCounts);
        }

        const counts = {};

        for (const category of categories) {
          const cName = category.name;
          const rawCounts =
            countsByCategory[cName] ||
            Object.entries(countsByCategory).find(
              ([name]) => normalizeName(name) === normalizeName(cName)
            )?.[1] ||
            {};

          const normalizedRawCounts = Object.fromEntries(
            Object.entries(rawCounts).map(([name, count]) => [
              normalizeName(name),
              Number(count) || 0,
            ])
          );

          const mappedCounts = {};

          if (category.features.length > 0) {
            category.features.forEach((f) => {
              mappedCounts[f.name] =
                rawCounts[f.name] ?? normalizedRawCounts[normalizeName(f.name)] ?? 0;
            });
          } else {
            mappedCounts.total = Object.values(rawCounts).reduce(
              (sum, v) => sum + (v || 0),
              0
            );
          }

          counts[category.id] = mappedCounts;
        }

        setFeatureCounts(counts);
      }
    } catch (error) {
      console.error("Error fetching feature counts:", error);
    }
  };

  useEffect(() => {
    if (categories.length > 0) {
      fetchFeatureCounts();
    }
  }, [categories]);

  // Fetch total user count from companyConfig/mobileAppLimits/currentUserCount (same pattern as featureCounts)
  useEffect(() => {
    const fetchTotalUserCount = async () => {
      try {
        const configRef = doc(db, "companyConfig", "mobileAppLimits");
        const snapshot = await getDoc(configRef);
        if (snapshot.exists()) {
          const data = snapshot.data();
          setTotalUserCount(Number(data?.currentUserCount) || 0);
        } else {
          setTotalUserCount(0);
        }
      } catch (error) {
        console.error("Error fetching total user count:", error);
        setTotalUserCount(0);
      }
    };

    fetchTotalUserCount();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await authenticatedFetch(
          `${BACKEND_URL}/api/project/get_common_categories`,
          {
            method: "GET",
          }
        );

        const result = await res.json();

        // Backend response contains data.categories
        if (result.success && result.data && result.data.categories) {
          setCommonCategories(result.data.categories);
        } else {
          console.error("Invalid response format:", result);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const savedCategories = localStorage.getItem("formData2");
    if (savedCategories) {
      setCategories(JSON.parse(savedCategories));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("formData2", JSON.stringify(categories));
  }, [categories]);

  const commonCategoryNames = Object.keys(commonCategories);
  const allCommonFeatures = Object.values(commonCategories).flat();

  function getValue(inputValue) {
    setValue(inputValue);
  }

  useEffect(() => {
    if (value) {
      switch (valueType) {
        case "Enter New Category":
          if (Array.isArray(value)) {
            addCategories(value);
          } else {
            addCategory(value);
          }
          break;
        case "Enter New Feature":
          addFeature();
          break;
        default:
          break;
      }
      setValue("");
      setValueType("");
    }
  }, [value]);

  function addCategories(values) {
    values.forEach((categoryName) => {
      const id = generateId();
      setCategories((prev) => [
        ...prev,
        { id: id, name: categoryName, features: [] },
      ]);
    });
  }

  function addCategory(name) {
    const id = generateId();
    setCategories((prev) => [...prev, { id: id, name: name, features: [] }]);
  }

  function addFeature() {
    if (currentCategoryId) {
      setCategories((prev) =>
        prev.map((category) => {
          if (category.id === currentCategoryId) {
            if (Array.isArray(value)) {
              if (value.length > 0) {
                const firstFeature = { id: generateId(), name: value[0] };
                category.features.push(firstFeature);
              }

              for (let index = 1; index < value.length; index++) {
                const newCategoryName = `${category.name}`;
                const newCategory = {
                  id: generateId(),
                  name: newCategoryName,
                  features: [],
                };
                const newFeature = { id: generateId(), name: value[index] };
                newCategory.features.push(newFeature);
                setCategories((prevCategories) => [
                  ...prevCategories,
                  newCategory,
                ]);
              }
            } else {
              category.features.push({ id: generateId(), name: value });
            }

            return { ...category };
          }
          return category;
        })
      );
    }
  }

  const generateId = () => {
    return Math.random().toString(36).substring(2, 9);
  };

  function handleSubmit() {
    const hasEmptyFeatures = categories.some(
      (category) => category.features.length === 0
    );
    if (hasEmptyFeatures) {
      setError("Each category must have at least one feature.");
      return;
    }

    setError("");
    localStorage.setItem("formData2", JSON.stringify(categories));
    navigate("/form3");
  }

  function handlePrevious() {
    navigate("/form1");
  }

  function handleCancel() {
    const storedData = JSON.parse(localStorage.getItem("form1Categories"));
    console.log("storedData", storedData);

    const storedForm2Data = JSON.parse(localStorage.getItem("formData2"));
    console.log("storedForm2Data", storedForm2Data);
    if (storedData && storedData.length > 0) {
      storedData.forEach((category) => {
        if (category.features && category.features.length > 0) {
          category.features.forEach((feature) => {
            // Check if this feature is an image
            if (category.name === "Image" && feature.name) {
              const imageURL = feature.name;

              // Check if imageURL is defined and not just a space
              if (imageURL && imageURL.trim() !== "") {
                try {
                  const imageRef = ref(storage, imageURL);

                  // Delete the image from Firebase Storage
                  deleteObject(imageRef)
                    .then(() => {
                      console.log("Image deleted successfully:", imageURL);
                    })
                    .catch((error) => {
                      console.error(
                        "Error occurred while deleting image:",
                        error
                      );
                    });
                } catch (error) {
                  console.error("Error handling image URL:", error);
                }
              } else {
                console.log(
                  "No valid image URL found for item, skipping deletion."
                );
              }
            }
          });
        }
      });
    }

    localStorage.removeItem("form1Categories");
    localStorage.removeItem("form1Question");
    localStorage.removeItem("questionFeatures");
    localStorage.removeItem("formData2");
    localStorage.removeItem("priceCurrency");
    navigate("/");
  }

  const sortedCategories = [...categories]
    .sort((a, b) => {
      const featureA = a.features.length > 0 ? a.features[0].name : "";
      const featureB = b.features.length > 0 ? b.features[0].name : "";
      return featureA.localeCompare(featureB);
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleDeleteCategory = (id) => {
    setCategories((prevCategories) =>
      prevCategories.filter((category) => category.id !== id)
    );
  };

  return (
    <>
      <header>
        <span></span>
        <img src={logo} alt="Linesol" style={{ height: "44px" }} />
        <span></span>
      </header>
      <div className={styles.container}>
        {isPopupVisible && (
          <Popup
            getValue={getValue}
            valueType={valueType}
            category={
              valueType === "Enter New Category" ? commonCategoryNames : null
            }
            feature={
              valueType === "Enter New Feature"
                ? commonCategories[
                    categories.find((cat) => cat.id === currentCategoryId)?.name
                  ] || []
                : null
            }
            selectedCategory={
              categories.find((cat) => cat.id === currentCategoryId)?.name
            }
            setIsPopupVisible={setIsPopupVisible}
          />
        )}

        <div
          className={styles.form}
          style={{
            boxShadow: theme
              ? "rgba(52, 51, 51, 0.44) 0px 0px 0px 2px" // border for dark mode
              : "rgba(201, 201, 201, 0.23) 0px 0px 0px 2px", // border for light mode
          }}
        >
          <div className={styles.headerTop}>
            <p>Question Features</p>
            <p style={{ color: "#007bff" }}>User Features</p>
            <p>Study Metadata</p>
          </div>

          <p style={{ marginTop: "0px", textAlign: "justify" }}>
            Specify the user features to be investigated in the study. Search
            existing categories of user features or add custom ones. Define the possible values participants are permitted to have to be included in your study. 
          </p>
          <p style={{ marginTop: "0px", textAlign: "justify" }}>
            Examine the 'Users' column to view the number of all participants. This helps understand the overall diversity and representation available for your study.
          </p>
          <p>
            Get results from a pool of{" "}
            <span
              style={{
                textDecoration: "underline dotted",
                cursor: "pointer",
                position: "relative",
                display: "inline-block",
              }}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onClick={() => setShowTooltip((prev) => !prev)}
            >
              {totalUserCount}
              {showTooltip && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    marginBottom: "8px",
                    padding: "6px 10px",
                    background: "#333",
                    color: "#fff",
                    fontSize: "12px",
                    borderRadius: "4px",
                    whiteSpace: "nowrap",
                    zIndex: 100,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                  }}
                >
                  Users currently signed up to the mobile app
                  {/* tooltip arrow */}
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      border: "6px solid transparent",
                      borderTopColor: "#333",
                    }}
                  />
                </div>
              )}
            </span>{" "}
            participants, with a maximum of 40% overrepresentation for any single
            demographic.
          </p>
          <table
            className="category-table"
            style={{
              border: `1px solid`,
              borderColor: theme ? "#e1e1e1" : "#4A4A4A",
              borderCollapse: "collapse",
              marginTop: "10px",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    width: "auto",
                    border: `1px solid`,
                    borderColor: theme ? "#e1e1e1" : "#4A4A4A",
                  }}
                >
                  Category
                </th>
                <th
                  style={{
                    width: "auto",
                    border: `1px solid`,
                    borderColor: theme ? "#e1e1e1" : "#4A4A4A",
                  }}
                >
                  Features
                </th>
                <th
                  style={{
                    width: "auto",
                    border: `1px solid`,
                    borderColor: theme ? "#e1e1e1" : "#4A4A4A",
                  }}
                >
                  Users
                </th>
                <th
                  style={{
                    width: "20px",
                    border: `1px solid`,
                    borderColor: theme ? "#e1e1e1" : "#4A4A4A",
                  }}
                >
                  Delete
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedCategories.map((category) => (
                <tr
                  key={category.id}
                  style={{
                    border: `1px solid`,
                    borderColor: theme ? "#e1e1e1" : "#4A4A4A",
                  }}
                >
                  <td
                    style={{
                      color: theme ? "white" : "black",
                      border: `1px solid`,
                      borderColor: theme ? "#e1e1e1" : "#4A4A4A",
                    }}
                  >
                    {category.name}
                  </td>
                  <td
                    style={{
                      color: "#fff",
                      border: `1px solid`,
                      borderColor: theme ? "#e1e1e1" : "#4A4A4A",
                    }}
                  >
                    {category.features.map((feature) => (
                      <span key={feature.id}>{feature.name}</span>
                    ))}
                    {category.features.length === 0 && (
                      <Link
                        onClick={() => {
                          setIsPopupVisible(true);
                          setValueType("Enter New Feature");
                          setCurrentCategoryId(category.id);
                        }}
                        style={{ color: "#007bff" }}
                      >
                        + New Feature
                      </Link>
                    )}
                  </td>
                  <td
                    style={{
                      border: `1px solid`,
                      borderColor: theme ? "#e1e1e1" : "#4A4A4A",
                    }}
                  >
                    {category.features.length === 0 ? (
                      <span>{featureCounts[category.id]?.total || 0}</span>
                    ) : (
                      category.features.map((feature) => (
                        <span key={feature.id}>
                          {featureCounts[category.id]?.[feature.name] || 0}
                        </span>
                      ))
                    )}
                  </td>
                  <td
                    className="delete-cell"
                    style={{
                      border: `1px solid`,
                      borderColor: theme ? "#e1e1e1" : "#4A4A4A",
                    }}
                  >
                    <button
                      style={{ width: "30px", height: "25px" }}
                      className="delete-button"
                      onClick={() => handleDeleteCategory(category.id)}
                    >
                      X
                    </button>
                  </td>
                </tr>
              ))}
              <tr
                style={{
                  border: `1px solid`,
                  borderColor: theme ? "#e1e1e1" : "#4A4A4A",
                }}
              >
                <td
                  style={{
                    border: `1px solid`,
                    borderColor: theme ? "#e1e1e1" : "#4A4A4A",
                  }}
                >
                  <Link
                    onClick={() => {
                      setIsPopupVisible(true);
                      setValueType("Enter New Category");
                    }}
                    style={{ color: "#007bff" }}
                  >
                    + New Category
                  </Link>
                </td>
                <td
                  style={{
                    border: `1px solid`,
                    borderColor: theme ? "#e1e1e1" : "#4A4A4A",
                  }}
                ></td>
                <td
                  style={{
                    border: `1px solid`,
                    borderColor: theme ? "#e1e1e1" : "#4A4A4A",
                  }}
                ></td>
                <td
                  style={{
                    border: `1px solid`,
                    borderColor: theme ? "#e1e1e1" : "#4A4A4A",
                  }}
                ></td>
              </tr>
            </tbody>
          </table>

          <div className="error-text" style={{ color: "red" }}>
            {error}
          </div>

          <div style={{ display: "flex", columnGap: "10px", marginTop: "0px" }}>
            <button onClick={handlePrevious} style={{ padding: "10px 20px" }}>
              Previous
            </button>
            <button
              onClick={handleCancel}
              style={{
                padding: "5px 20px",
                border: "none",
                borderRadius: "5px",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              style={{
                padding: "5px 20px",
                border: "none",
                borderRadius: "5px",
              }}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Form4;
